import { useEffect, useRef, useState } from "react";
import { api, type BillingView, type LaunchState } from "../api";
import { GIB, newIdempotencyKey, sizeLabel } from "../billing";
import { errorText, useT } from "../i18n";
import { formatDay } from "../time";
import { Disclosure } from "../Disclosure";

/** The launch (Legacy) switch: who new members become. The cap is fixed and a used seat is never freed. */
export function LaunchCard() {
  const t = useT();
  const [state, setState] = useState<LaunchState | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.adminSettings().then((s) => setState(s.launch), (err) => setError(errorText(err))); }, []);

  async function toggle() {
    if (!state) return;
    if (!confirm(state.switch ? t.adminBilling.confirmLaunchClose : t.adminBilling.confirmLaunchOpen)) return;
    setError(null);
    try {
      setState((await api.setLaunchOpen(!state.switch)).launch);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Disclosure title={t.adminBilling.launchTitle} open>
      {!state ? <p>{t.common.loading}</p> : (
        <>
          <p>{t.adminBilling.launchLine(state.count, state.cap)} {state.open ? t.adminBilling.launchOn : state.switch ? t.adminBilling.launchFull : t.adminBilling.launchOffSwitch}</p>
          <button onClick={() => void toggle()}>{state.switch ? t.adminBilling.launchClose : t.adminBilling.launchOpen}</button>
          <p className="small muted">{t.adminBilling.launchNote}</p>
        </>
      )}
      {error && <p className="error small" role="alert">{error}</p>}
    </Disclosure>
  );
}

/** One member's plan, stamps and rights for the admin, and the hand tools. `canChange`: the roles of 4.22 allow it. */
export function MemberBilling({ memberId, canChange }: { memberId: number; canChange: boolean }) {
  const t = useT();
  const [view, setView] = useState<BillingView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      setView(await api.adminMemberBilling(memberId));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
    setLoading(false);
  };

  // A key per kind of change, kept until it succeeds: a retry of the same form is the same request,
  // with the same moment for any end it computes (PLAN.md 4.27). A changed form is a new request.
  const keys = useRef(new Map<string, { key: string; form: string; at: number }>());
  const keyFor = (kind: string, form = "") => {
    const kept = keys.current.get(kind);
    if (kept && kept.form === form) return kept;
    const fresh = { key: newIdempotencyKey(kind), form, at: Math.floor(Date.now() / 1000) };
    keys.current.set(kind, fresh);
    return fresh;
  };
  const done = async (kind: string) => {
    keys.current.delete(kind);
    setNotice(t.adminBilling.done);
    await load();
  };
  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const [units, setUnits] = useState("");
  const [stampReason, setStampReason] = useState("");
  const [plan, setPlan] = useState<"start" | "legacy" | "free">("start");
  const [days, setDays] = useState("");
  const [rightReason, setRightReason] = useState("");
  const [gib, setGib] = useState("");
  const [storageReason, setStorageReason] = useState("");
  const inDays = (n: string, from: number) => (n.trim() === "" ? undefined : from + Math.round(Number(n) * 86400));

  return (
    <Disclosure title={t.adminBilling.memberTitle} className="member-billing">
      {!view && <button type="button" className="small" disabled={loading} onClick={() => void load()}>{loading ? t.adminBilling.memberLoading : t.adminBilling.memberShow}</button>}
      {error && <p className="error small" role="alert">{error}</p>}
      {notice && <p className="note small" role="status">{notice}</p>}
      {view && (
        <>
          <p className="small"><strong>{t.adminBilling.memberLine(t.billing.planNames[view.plan.id] ?? view.plan.id, view.stamps.remaining, sizeLabel(view.storage.used_bytes), sizeLabel(view.storage.quota_bytes))}</strong></p>
          {(view.provider_subscription || (view.provider_customers?.length ?? 0) > 0) && (
            <p className="small">
              {t.adminPayments.memberIds}:{" "}
              <code>{[...(view.provider_customers ?? []).map((c) => `${c.provider} ${c.customer_id}`), ...(view.provider_subscription ? [`${view.provider_subscription.provider} ${view.provider_subscription.subscription_id} (${view.provider_subscription.price_id})`] : [])].join(" · ")}</code>
            </p>
          )}
          <p className="small"><strong>{t.adminBilling.rights}</strong></p>
          {view.entitlements.length === 0 && <p className="small muted">{t.adminBilling.noRights}</p>}
          <ul className="rows">
            {view.entitlements.map((e) => (
              <li key={e.id}>
                <span className="grow small">
                  {t.billing.rightSource[e.source] ?? e.source} · {formatDay(e.starts_at * 1000)}{e.ends_at ? ` → ${formatDay(e.ends_at * 1000)}` : ""}
                  {e.reason ? <><br /><span className="muted">{t.adminBilling.why(e.reason)}</span></> : null}
                </span>
                {canChange && e.source === "manual" && (
                  <button className="link small" onClick={() => {
                    if (!confirm(t.adminBilling.confirmEnd)) return;
                    const reason = prompt(t.adminBilling.reason)?.trim() ?? "";
                    if (reason.length < 3 || reason.length > 200) { setError(t.adminBilling.reasonNeeded); return; }
                    void run(async () => { await api.adminEndRight(e.id, { reason, idempotency_key: keyFor(`end${e.id}`, reason).key }); await done(`end${e.id}`); });
                  }}>{t.adminBilling.endRight}</button>
                )}
              </li>
            ))}
          </ul>
          {!canChange && <p className="small muted">{t.adminBilling.notYours}</p>}
          {canChange && (
            <div className="member-billing-forms">
              <form className="small" onSubmit={(e) => { e.preventDefault(); void run(async () => { await api.adminGrantStamps(memberId, { units: Number(units), reason: stampReason.trim(), idempotency_key: keyFor("stamps", `${units}|${stampReason.trim()}`).key }); setUnits(""); setStampReason(""); await done("stamps"); }); }}>
                <strong>{t.adminBilling.addStamps}</strong>
                <div className="form-row">
                  <label>{t.adminBilling.stamps}<input type="number" min={1} max={10000} value={units} onChange={(e) => setUnits(e.target.value)} required /></label>
                  <label>{t.adminBilling.reason}<input value={stampReason} onChange={(e) => setStampReason(e.target.value)} required minLength={3} maxLength={200} /></label>
                  <button type="submit" className="small">{t.adminBilling.add}</button>
                </div>
              </form>
              <form className="small" onSubmit={(e) => { e.preventDefault(); void run(async () => { const req = keyFor("right", `${plan}|${days}|${rightReason.trim()}`); const ends_at = inDays(days, req.at); await api.adminGrantRight(memberId, { plan_id: plan, reason: rightReason.trim(), idempotency_key: req.key, ...(ends_at ? { ends_at } : {}) }); setRightReason(""); setDays(""); await done("right"); }); }}>
                <strong>{t.adminBilling.giveRight}</strong>
                <div className="form-row">
                  <label>{t.adminBilling.plan}<select value={plan} onChange={(e) => setPlan(e.target.value as typeof plan)}>{(["start", "legacy", "free"] as const).map((p) => <option key={p} value={p}>{t.billing.planNames[p]}</option>)}</select></label>
                  <label>{t.adminBilling.days}<input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} /></label>
                  <label>{t.adminBilling.reason}<input value={rightReason} onChange={(e) => setRightReason(e.target.value)} required minLength={3} maxLength={200} /></label>
                  <button type="submit" className="small">{t.adminBilling.give}</button>
                </div>
              </form>
              <form className="small" onSubmit={(e) => { e.preventDefault(); void run(async () => { await api.adminGrantStorage(memberId, { bytes: Math.round(Number(gib) * GIB), reason: storageReason.trim(), idempotency_key: keyFor("storage", `${gib}|${storageReason.trim()}`).key }); setGib(""); setStorageReason(""); await done("storage"); }); }}>
                <strong>{t.adminBilling.addStorage}</strong>
                <div className="form-row">
                  <label>{t.adminBilling.gib}<input type="number" min={1} max={100} step="any" value={gib} onChange={(e) => setGib(e.target.value)} required /></label>
                  <label>{t.adminBilling.reason}<input value={storageReason} onChange={(e) => setStorageReason(e.target.value)} required minLength={3} maxLength={200} /></label>
                  <button type="submit" className="small">{t.adminBilling.add}</button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </Disclosure>
  );
}
